import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const GstPayableLedger = () => {
  const [gstRecords, setGstRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingGst, setViewingGst] = useState(null);
  const [entries, setEntries] = useState([
    {
      id: Date.now(),
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceNumber: '',
      partyName: '',
      transactionType: 'sales',
      taxableAmount: '',
      gstRate: '18',
      cgstAmount: '',
      sgstAmount: '',
      igstAmount: '',
      totalGstAmount: '',
      totalAmount: '',
      paymentStatus: 'pending',
      paymentDate: ''
    }
  ]);
  const [expandedEntry, setExpandedEntry] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Auto-calculate GST amounts for all entries
  useEffect(() => {
    setEntries(prev => prev.map(entry => {
      const taxable = parseFloat(entry.taxableAmount) || 0;
      const rate = parseFloat(entry.gstRate) || 0;
      const gstAmount = (taxable * rate) / 100;

      // For intra-state: CGST + SGST (each half of total GST)
      // For inter-state: IGST (full GST amount)
      const cgst = gstAmount / 2;
      const sgst = gstAmount / 2;
      const igst = 0; // Set to gstAmount for inter-state transactions

      const totalGst = cgst + sgst + igst;
      const total = taxable + totalGst;

      return {
        ...entry,
        cgstAmount: cgst.toFixed(2),
        sgstAmount: sgst.toFixed(2),
        igstAmount: igst.toFixed(2),
        totalGstAmount: totalGst.toFixed(2),
        totalAmount: total.toFixed(2)
      };
    }));
  }, []);

  useEffect(() => {
    fetchGstRecords();
  }, []);

  const fetchGstRecords = async () => {
    try {
      const db = getDatabase();
      const gstRef = ref(db, 'HTAMS/expenses/gstpayable');
      const snapshot = await get(gstRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const gstList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setGstRecords(gstList);
      } else {
        setGstRecords([]);
      }
    } catch (error) {
      console.error('Error fetching GST records:', error);
      toast.error('Failed to fetch GST data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    
    // Validation for invoice number - alphanumeric only
    if (name === 'invoiceNumber') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [name]: alphanumericValue };
        return updated;
      });
      return;
    }

    // Validation for amount fields
    if (name === 'taxableAmount') {
      // Only allow numbers and decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setEntries(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [name]: validValue };
        
        // Auto-calculate GST amounts
        const taxable = parseFloat(validValue) || 0;
        const rate = parseFloat(updated[index].gstRate) || 0;
        const gstAmount = (taxable * rate) / 100;
        const cgst = gstAmount / 2;
        const sgst = gstAmount / 2;
        const igst = 0;
        const totalGst = cgst + sgst + igst;
        const total = taxable + totalGst;

        updated[index].cgstAmount = cgst.toFixed(2);
        updated[index].sgstAmount = sgst.toFixed(2);
        updated[index].igstAmount = igst.toFixed(2);
        updated[index].totalGstAmount = totalGst.toFixed(2);
        updated[index].totalAmount = total.toFixed(2);
        
        return updated;
      });
      return;
    }

    // Handle GST rate change
    if (name === 'gstRate') {
      setEntries(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [name]: value };
        
        // Auto-calculate GST amounts
        const taxable = parseFloat(updated[index].taxableAmount) || 0;
        const rate = parseFloat(value) || 0;
        const gstAmount = (taxable * rate) / 100;
        const cgst = gstAmount / 2;
        const sgst = gstAmount / 2;
        const igst = 0;
        const totalGst = cgst + sgst + igst;
        const total = taxable + totalGst;

        updated[index].cgstAmount = cgst.toFixed(2);
        updated[index].sgstAmount = sgst.toFixed(2);
        updated[index].igstAmount = igst.toFixed(2);
        updated[index].totalGstAmount = totalGst.toFixed(2);
        updated[index].totalAmount = total.toFixed(2);
        
        return updated;
      });
      return;
    }

    setEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const addNewEntry = () => {
    const newEntry = {
      id: Date.now(),
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceNumber: '',
      partyName: '',
      transactionType: 'sales',
      taxableAmount: '',
      gstRate: '18',
      cgstAmount: '',
      sgstAmount: '',
      igstAmount: '',
      totalGstAmount: '',
      totalAmount: '',
      paymentStatus: 'pending',
      paymentDate: ''
    };
    setEntries([...entries, newEntry]);
    setExpandedEntry(entries.length);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      const updated = entries.filter((_, i) => i !== index);
      setEntries(updated);
      if (expandedEntry >= updated.length) {
        setExpandedEntry(updated.length - 1);
      }
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? -1 : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    if (!entry.invoiceDate) {
      toast.error(`Entry ${entryNum}: Invoice date is required`);
      return false;
    }
    if (!entry.invoiceNumber || !entry.invoiceNumber.trim()) {
      toast.error(`Entry ${entryNum}: Invoice number is required`);
      return false;
    }
    if (!entry.partyName || !entry.partyName.trim()) {
      toast.error(`Entry ${entryNum}: Party name is required`);
      return false;
    }
    if (!entry.transactionType) {
      toast.error(`Entry ${entryNum}: Transaction type is required`);
      return false;
    }
    if (!entry.taxableAmount || entry.taxableAmount.trim() === '') {
      toast.error(`Entry ${entryNum}: Taxable amount is required`);
      return false;
    }
    if (parseFloat(entry.taxableAmount) <= 0) {
      toast.error(`Entry ${entryNum}: Taxable amount must be greater than 0`);
      return false;
    }
    if (entry.paymentStatus === 'paid' && !entry.paymentDate) {
      toast.error(`Entry ${entryNum}: Payment date is required when status is paid`);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setEntries([
      {
        id: Date.now(),
        invoiceDate: new Date().toISOString().split('T')[0],
        invoiceNumber: '',
        partyName: '',
        transactionType: 'sales',
        taxableAmount: '',
        gstRate: '18',
        cgstAmount: '',
        sgstAmount: '',
        igstAmount: '',
        totalGstAmount: '',
        totalAmount: '',
        paymentStatus: 'pending',
        paymentDate: ''
      }
    ]);
    setExpandedEntry(0);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        return;
      }
    }
    
    setSubmitting(true);

    try {
      const db = getDatabase();
      const gstRef = ref(db, 'HTAMS/expenses/gstpayable');

      if (editingId) {
        // Update single entry
        const recordRef = ref(db, `HTAMS/expenses/gstpayable/${editingId}`);
        const { id, ...entryData } = entries[0];
        await update(recordRef, entryData);
        toast.success('GST record updated successfully!');
      } else {
        // Create multiple entries
        const savePromises = entries.map(entry => {
          const { id, ...entryData } = entry;
          const newRecordRef = push(gstRef);
          return set(newRecordRef, entryData);
        });
        await Promise.all(savePromises);
        toast.success(`${entries.length} GST Record${entries.length > 1 ? 's' : ''} added successfully!`);
      }

      resetForm();
      fetchGstRecords();
    } catch (error) {
      console.error('Error saving GST record:', error);
      toast.error('Failed to save GST record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEntries([
      {
        id: Date.now(),
        invoiceDate: record.invoiceDate,
        invoiceNumber: record.invoiceNumber,
        partyName: record.partyName,
        transactionType: record.transactionType,
        taxableAmount: record.taxableAmount,
        gstRate: record.gstRate,
        cgstAmount: record.cgstAmount,
        sgstAmount: record.sgstAmount,
        igstAmount: record.igstAmount,
        totalGstAmount: record.totalGstAmount,
        totalAmount: record.totalAmount,
        paymentStatus: record.paymentStatus,
        paymentDate: record.paymentDate
      }
    ]);
    setExpandedEntry(0);
    setEditingId(record.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, invoiceNo) => {
    if (window.confirm(`Are you sure you want to delete GST record "${invoiceNo}"?`)) {
      try {
        const db = getDatabase();
        const recordRef = ref(db, `HTAMS/expenses/gstpayable/${id}`);
        await remove(recordRef);
        toast.success('GST record deleted successfully!');
        fetchGstRecords();
      } catch (error) {
        console.error('Error deleting GST record:', error);
        toast.error('Failed to delete GST record');
      }
    }
  };

  const handleView = (record) => {
    setViewingGst(record);
  };

  const closeViewModal = () => {
    setViewingGst(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const filteredRecords = gstRecords.filter(record => {
    const matchesSearch = record.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.partyName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = transactionTypeFilter === 'all' || record.transactionType === transactionTypeFilter;
    const matchesDate = (!dateFilter.start || new Date(record.invoiceDate) >= new Date(dateFilter.start)) &&
                       (!dateFilter.end || new Date(record.invoiceDate) <= new Date(dateFilter.end));
    return matchesSearch && matchesType && matchesDate;
  });

  const totalCGST = filteredRecords.reduce((sum, record) => sum + (parseFloat(record.cgstAmount) || 0), 0);
  const totalSGST = filteredRecords.reduce((sum, record) => sum + (parseFloat(record.sgstAmount) || 0), 0);
  const totalIGST = filteredRecords.reduce((sum, record) => sum + (parseFloat(record.igstAmount) || 0), 0);
  const totalGST = totalCGST + totalSGST + totalIGST;

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportToExcel = () => {
    if (filteredRecords.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredRecords.map((record, index) => ({
      'Sr No': index + 1,
      'Invoice Date': formatDate(record.invoiceDate),
      'Invoice Number': record.invoiceNumber || '-',
      'Party Name': record.partyName || '-',
      'Transaction Type': record.transactionType?.toUpperCase() || '-',
      'Taxable Amount': record.taxableAmount,
      'GST Rate (%)': record.gstRate,
      'CGST Amount': record.cgstAmount,
      'SGST Amount': record.sgstAmount,
      'IGST Amount': record.igstAmount,
      'Total GST Amount': record.totalGstAmount,
      'Total Amount': record.totalAmount,
      'Payment Status': record.paymentStatus?.toUpperCase() || '-',
      'Payment Date': formatDate(record.paymentDate) || '-'
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `GST_Payable_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredRecords.length} entries successfully!`);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, transactionTypeFilter, dateFilter]);

  if (loading) return <div className="loading">Loading GST records...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h2>GST Payable Ledger</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add GST Entry'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit GST Entry' : 'Add New GST Entry'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => (
              <div key={entry.id} style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                <div 
                  style={{
                    background: expandedEntry === index ? '#2c5f2d' : '#f8f9fa',
                    color: expandedEntry === index ? 'white' : '#333',
                    padding: '12px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: '500'
                  }}
                >
                  <span 
                    onClick={() => toggleEntry(index)}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    Entry {index + 1} {entry.partyName && `- ${entry.partyName}`} {entry.invoiceNumber && `(${entry.invoiceNumber})`}
                  </span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {!editingId && entries.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEntry(index);
                        }}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        Remove
                      </button>
                    )}
                    <span 
                      onClick={() => toggleEntry(index)}
                      style={{ cursor: 'pointer', fontSize: '20px' }}
                    >
                      {expandedEntry === index ? '−' : '+'}
                    </span>
                  </div>
                </div>
                
                {expandedEntry === index && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(5, 1fr)', 
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div className="form-group">
                        <label>Invoice Date <span className="required">*</span></label>
                        <input
                          type="date"
                          name="invoiceDate"
                          value={entry.invoiceDate}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Invoice Number <span className="required">*</span></label>
                        <input
                          type="text"
                          name="invoiceNumber"
                          value={entry.invoiceNumber}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter invoice number"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Party Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="partyName"
                          value={entry.partyName}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter party name"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Transaction Type <span className="required">*</span></label>
                        <select
                          name="transactionType"
                          value={entry.transactionType}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        >
                          <option value="sales">Sales</option>
                          <option value="purchase">Purchase</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Taxable Amount <span className="required">*</span></label>
                        <input
                          type="number"
                          name="taxableAmount"
                          value={entry.taxableAmount}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter taxable amount"
                          step="0.01"
                          min="0"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>GST Rate (%) <span className="required">*</span></label>
                        <select
                          name="gstRate"
                          value={entry.gstRate}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        >
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>CGST Amount</label>
                        <input
                          type="text"
                          value={`₹${entry.cgstAmount}`}
                          readOnly
                          className="form-control readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>SGST Amount</label>
                        <input
                          type="text"
                          value={`₹${entry.sgstAmount}`}
                          readOnly
                          className="form-control readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>IGST Amount</label>
                        <input
                          type="text"
                          value={`₹${entry.igstAmount}`}
                          readOnly
                          className="form-control readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>Total GST Amount</label>
                        <input
                          type="text"
                          value={`₹${entry.totalGstAmount}`}
                          readOnly
                          className="form-control readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>Total Amount</label>
                        <input
                          type="text"
                          value={`₹${entry.totalAmount}`}
                          readOnly
                          className="form-control readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>Payment Status <span className="required">*</span></label>
                        <select
                          name="paymentStatus"
                          value={entry.paymentStatus}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>

                      {entry.paymentStatus === 'paid' && (
                        <div className="form-group">
                          <label>Payment Date</label>
                          <input
                            type="date"
                            name="paymentDate"
                            value={entry.paymentDate}
                            onChange={(e) => handleInputChange(e, index)}
                            className="form-control"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {!editingId && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Entry' : `Save ${entries.length} GST Record${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <p className="card-label">Total CGST</p>
            <h3 className="card-value">₹{totalCGST.toLocaleString()}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">💳</div>
          <div className="card-content">
            <p className="card-label">Total SGST</p>
            <h3 className="card-value">₹{totalSGST.toLocaleString()}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">🏦</div>
          <div className="card-content">
            <p className="card-label">Total IGST</p>
            <h3 className="card-value">₹{totalIGST.toLocaleString()}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <p className="card-label">Total GST Payable</p>
            <h3 className="card-value">₹{totalGST.toLocaleString()}</h3>
          </div>
        </div>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search by invoice number or party name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />
        <select
          value={transactionTypeFilter}
          onChange={(e) => setTransactionTypeFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Transactions</option>
          <option value="sales">Sales</option>
          <option value="purchase">Purchase</option>
        </select>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
            className="date-input"
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
            className="date-input"
          />
        </div>
        
        {(searchTerm || transactionTypeFilter !== 'all' || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setTransactionTypeFilter('all');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Invoice No</th>
              <th>Party Name</th>
              <th>Type</th>
              <th>Taxable Amount</th>
              <th>GST Rate</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>IGST</th>
              <th>Total GST</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.length > 0 ? (
              currentRecords.map((record, index) => (
                <tr key={record.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(record.invoiceDate)}</td>
                  <td>{record.invoiceNumber}</td>
                  <td>{record.partyName}</td>
                  <td>
                    <span className={`transaction-type-badge ${record.transactionType}`}>
                      {record.transactionType === 'sales' ? '📤 Sales' : '📥 Purchase'}
                    </span>
                  </td>
                  <td>₹{parseFloat(record.taxableAmount).toLocaleString()}</td>
                  <td>{record.gstRate}%</td>
                  <td>₹{parseFloat(record.cgstAmount).toLocaleString()}</td>
                  <td>₹{parseFloat(record.sgstAmount).toLocaleString()}</td>
                  <td>₹{parseFloat(record.igstAmount).toLocaleString()}</td>
                  <td>₹{parseFloat(record.totalGstAmount).toLocaleString()}</td>
                  <td>₹{parseFloat(record.totalAmount).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge ${record.paymentStatus}`}>
                      {record.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleView(record)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(record)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(record.id, record.invoiceNumber)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        
        {currentRecords.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>No GST records found</h3>
            <p>Add GST entries using the "Add GST Entry" button above.</p>
          </div>
        )}
      </div>

      {viewingGst && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>GST Record Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Invoice Date:</label>
                  <span>{formatDate(viewingGst.invoiceDate)}</span>
                </div>
                <div className="detail-item">
                  <label>Invoice Number:</label>
                  <span>{viewingGst.invoiceNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Party Name:</label>
                  <span>{viewingGst.partyName}</span>
                </div>
                <div className="detail-item">
                  <label>Transaction Type:</label>
                  <span className={`transaction-type-badge ${viewingGst.transactionType}`}>
                    {viewingGst.transactionType === 'sales' ? '📤 Sales' : '📥 Purchase'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Taxable Amount:</label>
                  <span>₹{parseFloat(viewingGst.taxableAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>GST Rate:</label>
                  <span>{viewingGst.gstRate}%</span>
                </div>
                <div className="detail-item">
                  <label>CGST Amount:</label>
                  <span>₹{parseFloat(viewingGst.cgstAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>SGST Amount:</label>
                  <span>₹{parseFloat(viewingGst.sgstAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>IGST Amount:</label>
                  <span>₹{parseFloat(viewingGst.igstAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Total GST Amount:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingGst.totalGstAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Total Amount:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingGst.totalAmount).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Status:</label>
                  <span className={`status-badge ${viewingGst.paymentStatus}`}>
                    {viewingGst.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Pending'}
                  </span>
                </div>
                {viewingGst.paymentStatus === 'paid' && viewingGst.paymentDate && (
                  <div className="detail-item">
                    <label>Payment Date:</label>
                    <span>{formatDate(viewingGst.paymentDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredRecords.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRecords.length)} of {filteredRecords.length} entries
          </div>
          <div className="pagination">
            <button 
              className="pagination-btn" 
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => paginate(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-dots">...</span>;
              }
              return null;
            })}
            
            <button 
              className="pagination-btn" 
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstPayableLedger;
